import React, { useEffect, useRef, useState } from 'react';
import { money } from './accountantModel';

function commitmentLabel(row) {
  return [row.commitment_number, row.commitment_name].filter(Boolean).join(' ');
}

function displayValue(commitments, excluded) {
  const leftOut = commitments.filter((row) => excluded.includes(row.vuid));
  if (!commitments.length) return 'No commitments on this project';
  if (!leftOut.length) return 'All commitments';
  if (leftOut.length === commitments.length) return 'No commitments included';
  const names = leftOut.map((row) => row.commitment_number || row.commitment_name).filter(Boolean);
  const shown = names.slice(0, 3).join(', ');
  const extra = names.length > 3 ? ` +${names.length - 3}` : '';
  return `All except ${shown}${extra}`;
}

export default function CommitmentFilter({
  id,
  open,
  setOpen,
  commitments,
  excluded,
  onChange,
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const anchorRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const excludedSet = new Set(excluded || []);
  const term = query.trim().toLowerCase();
  const visible = !term
    ? commitments
    : commitments.filter((row) => `${commitmentLabel(row)} ${row.vendor_name || ''}`.toLowerCase().includes(term));

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    if (open !== id) {
      setMenuStyle(null);
      return undefined;
    }
    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = Math.max(rect.width, 420);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 280 && rect.top > spaceBelow;
      setMenuStyle(openUp
        ? { position: 'fixed', left, width, bottom: window.innerHeight - rect.top + 2, zIndex: 9999 }
        : { position: 'fixed', left, width, top: rect.bottom + 2, zIndex: 9999 });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, id]);

  const toggle = (vuid) => {
    if (excludedSet.has(vuid)) onChange((excluded || []).filter((item) => item !== vuid));
    else onChange([...(excluded || []), vuid]);
  };

  return (
    <div className="mb-4">
      <div className="text-sm font-medium text-gray-700 mb-1">Commitments included</div>
      <div className="dropdown-container relative w-full md:w-[28rem]" ref={anchorRef}>
        <input
          type="text"
          readOnly
          disabled={disabled}
          className="pc-calc-select"
          value={displayValue(commitments, excluded || [])}
          placeholder="All commitments"
          onMouseDown={(event) => {
            if (disabled) return;
            event.preventDefault();
            window.dispatchEvent(new CustomEvent('pc-calc-picker', { detail: id }));
            setOpen(open === id ? '' : id);
          }}
        />
        {open === id && menuStyle ? (
          <div
            className="bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-auto"
            style={menuStyle}
          >
            <input
              type="text"
              placeholder="Search commitments"
              className="w-full px-3 py-2 border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={query}
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
            />
            <button
              type="button"
              className="w-full px-3 py-2 text-left hover:bg-gray-100 border-b border-gray-100"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onChange([])}
            >
              <span className="mr-2 inline-block w-4 text-gray-900">{excludedSet.size ? '' : '✓'}</span>
              <span className="font-medium text-gray-900">All commitments</span>
            </button>
            {visible.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
            ) : visible.map((row) => {
              const included = !excludedSet.has(row.vuid);
              return (
                <button
                  key={row.vuid}
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => toggle(row.vuid)}
                >
                  <span className="mr-2 inline-block w-4 text-gray-900">{included ? '✓' : ''}</span>
                  <span className="font-medium text-gray-900">{commitmentLabel(row)}</span>
                  <span className="mt-0.5 block pl-6 text-sm text-gray-600">
                    {[row.vendor_name, money(row.commitment_amount)].filter(Boolean).join(' · ')}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        The calculation uses the current amount of every commitment, including change orders. Uncheck a commitment to leave it out.
      </p>
    </div>
  );
}
